import { io, Socket } from 'socket.io-client';
import { getToken } from '@/lib/api/apiClient';

const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL || 'https://venuemarketer.com:8443';

const SOCKET_CLIENT_VERSION = '4.8.3';

let socket: Socket | null = null;
let joinedUserId: string | null = null;
let connectHandler: (() => void) | null = null;
let joinEmittedForUserId: string | null = null;

function socketOptions(token: string) {
  return {
    autoConnect: true,
    transports: ['websocket', 'polling'],
    query: { token },
    transportOptions: {
      polling: {
        extraHeaders: {
          Authorization: `Bearer ${token}`,
        },
      },
    },
  };
}

export function getSocket(): Socket | null {
  return socket;
}

export function getJoinedUserId(): string | null {
  return joinedUserId;
}

/** Client event name, e.g. `message.sent.abc-123` (from Redis VL-MessageSent). */
export function userScopedEvent(event: string, userId: string): string {
  return `${event}.${userId}`;
}

/** e.g. `message.updated.{messageId}` */
export function messageScopedEvent(event: string, messageId: string): string {
  return `${event}.${messageId}`;
}

/** Emit join — buffers until the socket connects. */
function emitJoin(s: Socket): void {
  if (!joinedUserId) return;
  if (joinEmittedForUserId === joinedUserId && s.connected) return;
  if (import.meta.env.DEV) {
    console.info(
      `[socket] client v${SOCKET_CLIENT_VERSION} emit join →`,
      joinedUserId,
      'connected:',
      s.connected
    );
  }
  s.emit('join', joinedUserId);
  if (s.connected) {
    joinEmittedForUserId = joinedUserId;
  }
}

function bindLifecycleHandlers(s: Socket): void {
  if (connectHandler) {
    s.off('connect', connectHandler);
  }

  connectHandler = () => {
    joinEmittedForUserId = null;
    emitJoin(s);
  };

  s.on('connect', connectHandler);

  if (import.meta.env.DEV) {
    s.off('connect_error');
    s.on('connect_error', (err: unknown) => {
      const e = err as {
        message?: string;
        type?: string;
        description?: unknown;
        data?: unknown;
      };
      console.warn('[socket] connect_error', {
        message: e?.message,
        type: e?.type,
        description:
          e?.description && typeof e.description === 'object'
            ? {
                status: (e.description as { status?: number }).status,
                statusText: (e.description as { statusText?: string }).statusText,
                readyState: (e.description as { readyState?: number }).readyState,
                responseText: (e.description as { responseText?: string })
                  .responseText,
              }
            : e?.description,
        data: e?.data,
        raw: err,
      });
    });
  }
}

function ensureSocket(): Socket | null {
  const token = getToken();
  if (!token) return null;

  if (socket?.connected) {
    bindLifecycleHandlers(socket);
    emitJoin(socket);
    return socket;
  }

  if (socket) {
    socket.io.opts.query = { token };
    socket.io.opts.transportOptions = socketOptions(token).transportOptions;
    bindLifecycleHandlers(socket);
    socket.connect();
    emitJoin(socket);
    return socket;
  }

  socket = io(SOCKET_URL, socketOptions(token));
  if (import.meta.env.DEV) {
    console.info(`[socket] client v${SOCKET_CLIENT_VERSION} ready, engine:`, SOCKET_URL);
  }
  bindLifecycleHandlers(socket);
  emitJoin(socket);
  return socket;
}

export function connectSocket(forUserId?: string): Socket | null {
  if (forUserId) joinedUserId = forUserId;
  return ensureSocket();
}

export function disconnectSocket(): void {
  if (!socket) return;

  if (connectHandler) {
    socket.off('connect', connectHandler);
    connectHandler = null;
  }

  socket.disconnect();
  socket = null;
  joinedUserId = null;
  joinEmittedForUserId = null;
}

/** Set user room; join is emitted immediately (queued until namespace connects). */
export function joinUserRoom(userId: string): void {
  joinedUserId = userId;
  const s = getSocket();
  if (s) {
    emitJoin(s);
  }
}

export function onUserSocketEvent<T = unknown>(
  event: string,
  userId: string,
  handler: (payload: T) => void
): () => void {
  const eventName = userScopedEvent(event, userId);
  const s = connectSocket();
  if (!s) return () => undefined;

  s.on(eventName, handler);
  return () => {
    s.off(eventName, handler);
  };
}

export function onMessageSocketEvent<T = unknown>(
  event: string,
  messageId: string,
  handler: (payload: T) => void
): () => void {
  return onSocketEvent<T>(messageScopedEvent(event, messageId), handler);
}

export function onSocketEvent<T = unknown>(
  event: string,
  handler: (payload: T) => void
): () => void {
  const s = connectSocket();
  if (!s) return () => undefined;

  const names = [event, event.startsWith('.') ? event.slice(1) : `.${event}`];
  for (const name of names) {
    s.on(name, handler);
  }

  return () => {
    for (const name of names) {
      s.off(name, handler);
    }
  };
}
